import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Button, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { sendWS, onVideoFrame } from '../services/websocket';
import { LiveMap } from '../components/LiveMap';
import api from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const MEDIA_BASE = API_BASE.replace(/\/api\/?$/, '');

function CallTimer({ startTime }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startTime) return;
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - startTime) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [startTime]);
  const m = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const s = String(elapsed % 60).padStart(2, '0');
  return <span>{m}:{s}</span>;
}

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { live } = useSelector((s) => s.vehicles);
  const vehicle = live[id];
  const [dbVehicle, setDbVehicle] = useState(null);
  const [violations, setViolations] = useState([]);
  const [message, setMessage] = useState('');
  const [videoActive, setVideoActive] = useState(false);
  const [videoHasFrame, setVideoHasFrame] = useState(false);
  const [videoCamera, setVideoCamera] = useState(null);
  const [videoRecording, setVideoRecording] = useState(false);
  const [videoStartTime, setVideoStartTime] = useState(null);
  const [videoClips, setVideoClips] = useState([]);
  const [videoExpanded, setVideoExpanded] = useState(false);
  const canvasRef = useRef(null);
  const canvasExpandedRef = useRef(null);

  // Double-buffer canvas render
  const renderFrame = useCallback((base64Frame) => {
    const targets = [canvasRef.current, canvasExpandedRef.current].filter(Boolean);
    targets.forEach((canvas) => {
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
      };
      img.src = `data:image/jpeg;base64,${base64Frame}`;
    });
  }, []);

  useEffect(() => {
    api.get(`/vehicles/${id}/`).then((r) => setDbVehicle(r.data)).catch(() => {});
    api.get(`/violations/?vehicle=${id}`).then((r) => setViolations(r.data.results || r.data)).catch(() => {});
  }, [id]);

  // Load recorded SOS video clips
  useEffect(() => {
    api.get(`/sos/`).then((r) => {
      const events = r.data.results || r.data;
      const vehicleSos = events.filter((e) => String(e.vehicle) === String(id));
      const activeOrRecent = vehicleSos.filter((e) => e.status === 'active' || e.video_clips_count > 0);
      activeOrRecent.forEach((sos) => {
        api.get(`/sos/${sos.id}/videos/`).then((r2) => {
          if (r2.data.length > 0) {
            setVideoClips((prev) => [...prev.filter((c) => c.sos_event !== sos.id), ...r2.data]);
          }
        }).catch(() => {});
      });
    }).catch(() => {});
  }, [id]);

  // Listen for live SOS video frames for this specific vehicle
  useEffect(() => {
    const handler = (msg) => {
      if (String(msg.vehicle_id) !== String(id)) return;
      if (msg.type === 'started') {
        setVideoActive(true);
        setVideoHasFrame(false);
        setVideoStartTime(Date.now());
      } else if (msg.type === 'frame') {
        setVideoHasFrame(true);
        setVideoCamera(msg.camera || null);
        renderFrame(msg.frame);
      } else if (msg.type === 'clip_uploading') {
        setVideoRecording(true);
      } else if (msg.type === 'clip_ready') {
        setVideoRecording(false);
      } else if (msg.type === 'stopped') {
        setVideoActive(false);
        setVideoHasFrame(false);
        setVideoExpanded(false);
      }
    };
    const unsub = onVideoFrame(handler);
    return unsub;
  }, [id, renderFrame]);

  const v = vehicle || dbVehicle || {};

  const handleSendMessage = () => {
    if (!message.trim()) return;
    sendWS('message.send', { text: message, priority: 'high', target_type: 'vehicle', vehicle_id: parseInt(id) });
    setMessage('');
  };

  return (
    <div style={{ padding: 20 }}>
      <Button variant="outline-secondary" size="sm" className="mb-3" onClick={() => navigate(-1)}>
        <i className="bi bi-arrow-left"></i> Retour
      </Button>

      <h4 style={{ color: '#fff', fontWeight: 700 }}>
        <i className="bi bi-truck" style={{ color: '#00e676' }}></i> Véhicule {v.plate || `#${id}`}
        {v.status === 'sos' && <span className="badge-sos ms-2">SOS ACTIF</span>}
      </h4>

      <Row className="g-3 mt-2">
        <Col md={6}>
          <Card style={{ background: '#1a1a2e', border: '1px solid #222' }}>
            <Card.Body>
              <h6 style={{ color: '#00e676' }}>Informations</h6>
              <table style={{ fontSize: 13, color: '#ccc' }}>
                <tbody>
                  <tr><td style={{ color: '#888', paddingRight: 16 }}>Conducteur</td><td>{v.driver_name || '-'}</td></tr>
                  <tr><td style={{ color: '#888', paddingRight: 16 }}>Plaque</td><td>{v.plate || '-'}</td></tr>
                  <tr><td style={{ color: '#888', paddingRight: 16 }}>Statut</td><td>{v.status || '-'}</td></tr>
                  <tr><td style={{ color: '#888', paddingRight: 16 }}>Vitesse actuelle</td>
                    <td style={{ fontWeight: 700, color: (v.speed || 0) > 100 ? '#ff1744' : '#00e676', fontSize: 18 }}>
                      {Math.round(v.speed || v.last_speed || 0)} km/h
                    </td>
                  </tr>
                  <tr><td style={{ color: '#888' }}>Position</td><td style={{ fontSize: 11 }}>{v.lat?.toFixed(5) || v.last_lat?.toFixed(5)}, {v.lng?.toFixed(5) || v.last_lng?.toFixed(5)}</td></tr>
                </tbody>
              </table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card style={{ background: '#1a1a2e', border: '1px solid #222' }}>
            <Card.Body>
              <h6 style={{ color: '#2979ff' }}>Envoyer un message</h6>
              <div className="d-flex gap-2">
                <input
                  className="form-control form-control-sm"
                  placeholder="Message au conducteur..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  style={{ background: '#0d0d1a', border: '1px solid #333', color: '#fff' }}
                />
                <Button size="sm" onClick={handleSendMessage} style={{ background: '#2979ff', border: 'none' }}>
                  <i className="bi bi-send"></i>
                </Button>
              </div>
              {v.status === 'sos' && (
                <Button
                  variant="danger"
                  size="sm"
                  className="w-100 mt-3"
                  onClick={() => sendWS('sos.respond', { vehicle_id: parseInt(id), message: 'Aide en route, restez en sécurité.' })}
                >
                  <i className="bi bi-megaphone"></i> Répondre au SOS
                </Button>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Violations */}
      <h5 style={{ color: '#fff', marginTop: 24, fontWeight: 600 }}>Violations récentes</h5>
      <Table variant="dark" size="sm" style={{ fontSize: 13 }}>
        <thead>
          <tr><th>Vitesse</th><th>Limite</th><th>Excès</th><th>Date</th></tr>
        </thead>
        <tbody>
          {violations.slice(0, 10).map((vl) => (
            <tr key={vl.id}>
              <td style={{ color: '#ff1744' }}>{Math.round(vl.recorded_speed)} km/h</td>
              <td>{vl.limit_kmh} km/h</td>
              <td style={{ color: '#ff9100' }}>+{Math.round(vl.recorded_speed - vl.limit_kmh)} km/h</td>
              <td>{new Date(vl.timestamp).toLocaleString('fr-FR')}</td>
            </tr>
          ))}
          {violations.length === 0 && <tr><td colSpan={4} style={{ color: '#555', textAlign: 'center' }}>Aucune violation</td></tr>}
        </tbody>
      </Table>

      {/* SOS Vidéo en direct — style appel vidéo Messenger/WhatsApp */}
      {(videoActive || v.status === 'sos') && (
        <>
          <style>{`
            @keyframes vd-ring1 { 0% { transform: scale(0.8); opacity: 0.5; } 100% { transform: scale(2.2); opacity: 0; } }
            @keyframes vd-ring2 { 0% { transform: scale(0.8); opacity: 0.3; } 100% { transform: scale(2.8); opacity: 0; } }
            @keyframes vd-ring3 { 0% { transform: scale(0.8); opacity: 0.15; } 100% { transform: scale(3.2); opacity: 0; } }
            @keyframes vd-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.06); } }
            @keyframes vd-dot { 0%,100% { opacity: 1; } 50% { opacity: 0.2; } }
            @keyframes vd-rec { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
            @keyframes vd-fadein { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
          `}</style>
          <h5 style={{ color: '#ff1744', marginTop: 24, fontWeight: 600 }}>
            <i className="bi bi-camera-video-fill"></i> Vidéo SOS en direct
          </h5>

          {/* Inline video card */}
          <div style={{
            background: '#000', border: '2px solid #ff1744', borderRadius: 12,
            overflow: 'hidden', marginBottom: 16, position: 'relative',
            cursor: videoHasFrame ? 'pointer' : 'default',
          }} onClick={() => videoHasFrame && setVideoExpanded(true)}>
            {videoHasFrame ? (
              /* Live — canvas with overlay */
              <div style={{ position: 'relative' }}>
                <canvas
                  ref={canvasRef}
                  style={{ width: '100%', display: 'block', maxHeight: 360, objectFit: 'cover', background: '#000' }}
                />
                {/* Top overlay gradient */}
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, padding: '8px 12px',
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  {videoRecording ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: '#d50000', color: '#fff', padding: '3px 10px',
                      borderRadius: 20, fontSize: 11, fontWeight: 700,
                    }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ff5252', animation: 'vd-rec 0.8s infinite' }}></span>
                      REC
                    </span>
                  ) : (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: 'rgba(255,23,68,0.9)', color: '#fff', padding: '3px 10px',
                      borderRadius: 20, fontSize: 11, fontWeight: 700,
                    }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff', animation: 'vd-dot 1.2s infinite' }}></span>
                      LIVE
                    </span>
                  )}
                  {videoCamera && (
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>
                      {videoCamera === 'back' ? '🛣️ Route' : '👤 Conducteur'}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                    <i className="bi bi-arrows-fullscreen"></i> Cliquez pour agrandir
                  </span>
                </div>
                {/* Bottom info */}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, padding: '30px 12px 8px',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
                    {v.plate || `Véhicule #${id}`}
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                    <CallTimer startTime={videoStartTime} />
                  </span>
                </div>
              </div>
            ) : (
              /* Connecting — pulsing rings WhatsApp style */
              <div style={{
                height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'radial-gradient(ellipse at center, #1a0a0e 0%, #000 70%)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <div style={{ position: 'relative', width: 80, height: 80 }}>
                    {[1, 2, 3].map((i) => (
                      <div key={i} style={{
                        position: 'absolute', inset: 0, borderRadius: '50%',
                        border: '2px solid rgba(255,23,68,0.3)',
                        animation: `vd-ring${i} 2s ease-out infinite`,
                        animationDelay: `${(i - 1) * 0.35}s`,
                      }} />
                    ))}
                    <div style={{
                      position: 'absolute', inset: 14, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #ff1744, #d50000)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 0 30px rgba(255,23,68,0.35)',
                      animation: 'vd-pulse 2s ease-in-out infinite',
                    }}>
                      <i className="bi bi-camera-video-fill" style={{ color: '#fff', fontSize: 22 }}></i>
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>
                      Connexion vidéo
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 2 }}>
                      Établissement du flux
                      <span style={{ animation: 'vd-dot 1s infinite' }}>...</span>
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10, marginTop: 6 }}>
                      {v.plate || `Véhicule #${id}`}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Fullscreen expanded video (Messenger style) */}
          {videoExpanded && videoHasFrame && (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 9999, background: '#000',
              display: 'flex', flexDirection: 'column',
              animation: 'vd-fadein 0.25s ease',
            }}>
              {/* Top bar */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)',
                padding: '16px 20px 40px', display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #ff1744, #d50000)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <i className="bi bi-truck" style={{ color: '#fff', fontSize: 18 }}></i>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>
                    {v.plate || `Véhicule #${id}`}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#00e676', animation: 'vd-dot 1.5s infinite' }}></span>
                    <CallTimer startTime={videoStartTime} />
                    {videoCamera && (
                      <span style={{ marginLeft: 4 }}>
                        · {videoCamera === 'back' ? 'Caméra route' : 'Caméra conducteur'}
                      </span>
                    )}
                  </div>
                </div>
                {/* LIVE / REC badge */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: videoRecording ? '#d50000' : 'rgba(255,23,68,0.9)',
                  padding: '4px 12px', borderRadius: 20, flexShrink: 0,
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: videoRecording ? '#ff5252' : '#fff',
                    animation: videoRecording ? 'vd-rec 0.8s infinite' : 'vd-dot 1.2s infinite',
                  }}></span>
                  <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>
                    {videoRecording ? 'REC' : 'LIVE'}
                  </span>
                </div>
              </div>

              {/* Full video canvas */}
              <canvas
                ref={canvasExpandedRef}
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000' }}
              />

              {/* Bottom bar — actions */}
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10,
                background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)',
                padding: '50px 20px 28px', display: 'flex', justifyContent: 'center', gap: 20,
              }}>
                {/* GPS */}
                {(v.lat || v.last_lat) && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{
                      width: 50, height: 50, borderRadius: '50%', background: 'rgba(255,255,255,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i className="bi bi-geo-alt-fill" style={{ color: '#2196f3', fontSize: 20 }}></i>
                    </div>
                    <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 9 }}>
                      {(v.lat || v.last_lat)?.toFixed(3)}, {(v.lng || v.last_lng)?.toFixed(3)}
                    </span>
                  </div>
                )}

                {/* Recording indicator */}
                {videoRecording && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{
                      width: 50, height: 50, borderRadius: '50%',
                      background: 'rgba(213,0,0,0.3)', border: '2px solid #ff1744',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      animation: 'vd-rec 1s infinite',
                    }}>
                      <div style={{ width: 16, height: 16, borderRadius: 4, background: '#ff1744' }}></div>
                    </div>
                    <span style={{ color: '#ff5252', fontSize: 10, fontWeight: 600 }}>REC</span>
                  </div>
                )}

                {/* Close / reduce */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <button onClick={() => setVideoExpanded(false)} style={{
                    width: 50, height: 50, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.15)', border: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}>
                    <i className="bi bi-arrows-angle-contract" style={{ color: '#fff', fontSize: 18 }}></i>
                  </button>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>Réduire</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Clips vidéo enregistrés */}
      {videoClips.length > 0 && (
        <>
          <h5 style={{ color: '#ff9100', marginTop: 24, fontWeight: 600 }}>
            <i className="bi bi-film"></i> Enregistrements vidéo SOS ({videoClips.length} clips)
          </h5>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {videoClips.map((clip) => {
              const videoUrl = clip.video_file?.startsWith('http') ? clip.video_file : `${MEDIA_BASE}${clip.video_file?.startsWith('/') ? '' : '/'}${clip.video_file}`;
              return (
                <div key={clip.id} style={{
                  background: '#1a1a2e', border: '1px solid #333', borderRadius: 8,
                  padding: 8, width: 200,
                }}>
                  <video
                    src={videoUrl}
                    controls
                    style={{ width: '100%', borderRadius: 6 }}
                  />
                  <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                    {new Date(clip.created_at).toLocaleString('fr-FR')}
                    {clip.duration_sec > 0 && ` — ${Math.round(clip.duration_sec)}s`}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Mini-map position temps réel */}
      {(v.lat || v.last_lat) && (
        <>
          <h5 style={{ color: '#fff', marginTop: 24, fontWeight: 600 }}>
            <i className="bi bi-geo-alt" style={{ color: '#00e676' }}></i> Position en temps réel
          </h5>
          <div style={{ height: 300, borderRadius: 12, overflow: 'hidden', border: '1px solid #222' }}>
            <LiveMap
              vehicles={{ [id]: { ...v, vehicle_id: parseInt(id) } }}
              center={[v.lat || v.last_lat, v.lng || v.last_lng]}
              zoom={15}
            />
          </div>
        </>
      )}
    </div>
  );
}
